import cn from 'classnames'
import Checkbox from 'ext-ui/components/Checkbox/Checkbox'
import TooltipIcon from 'ext-ui/components/TooltipIcon/TooltipIcon'
import React, { ReactElement } from 'react'
import AmountInputField from 'src/components/AmountInputField/AmountInputField'
import Box from 'src/components/Box/Box'
import FieldLabel from 'src/components/FieldLabel/FieldLabel'
import { ExtendCurrency } from 'src/enums'
import { useTranslate } from 'src/localization/Trns'

import style from './LowBalanceAlertField.style'

const TOOLTIP_TEXT =
  'When this feature is enabled, the card user will receive an alert when the available credit drops below a certain amount.'

export type LowBalanceAlertFieldProps = {
  isEnabled: boolean
  onEnabledChange: (next: boolean) => void
  thresholdDollars?: number
  onThresholdChange: (next: number | undefined) => void
  currency?: ExtendCurrency
  disabled?: boolean
  readOnly?: boolean
  error?: ReactElement | string
  className?: string
}

function LowBalanceAlertField(props: LowBalanceAlertFieldProps): ReactElement {
  const translate = useTranslate()
  const isInteractionBlocked = !!(props.disabled || props.readOnly)

  return (
    <Box
      className={cn('LowBalanceAlertField', props.className, {
        'is-disabled': isInteractionBlocked,
      })}
      css={style}
    >
      <div className="header-row">
        <FieldLabel>{translate('Low Available Credit Alert')}</FieldLabel>
        <TooltipIcon iconTestId="low-balance-alert-tooltip">
          {translate(TOOLTIP_TEXT)}
        </TooltipIcon>
      </div>

      <div className="checkbox-row">
        <Checkbox
          id="low-balance-alert-checkbox"
          aria-label={translate(
            'Get alerted when the virtual card available credit drops below a certain amount, so you can avoid declined transactions.',
          )}
          data-testid="low-balance-alert-checkbox"
          checked={props.isEnabled}
          disabled={isInteractionBlocked}
          onChange={(): void => {
            props.onEnabledChange(!props.isEnabled)
          }}
        />
        <span className="checkbox-label">
          {translate(
            'Get alerted when the virtual card available credit drops below a certain amount, so you can avoid declined transactions.',
          )}
        </span>
      </div>

      {props.isEnabled && (
        <div className="threshold-row">
          <AmountInputField
            fieldLabel={<>{translate('Low Available Credit Threshold')}</>}
            value={props.thresholdDollars}
            onChange={props.onThresholdChange}
            currency={props.currency}
            maxValue={99999999}
            isRequest={false}
            disabled={isInteractionBlocked}
            testId="low-balance-threshold-input"
            errors={props.error ? [props.error] : undefined}
          />
        </div>
      )}
    </Box>
  )
}

export default LowBalanceAlertField
